import { CloneItemDto } from '@nohara-dxdev/mt-common';
import { ApiResponse } from '@nohara-dxdev/oidc-core';
import { MutationConfig } from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { useMutation } from '@tanstack/react-query';

import { api } from '@/lib/api-client';

export type { CloneItemDto };

/**
 * MT1-1426: the clone POST endpoint now enqueues an asynchronous job and
 * returns the Job entity (single + batch). Success means "the clone started";
 * callers stash `id` and poll `GET /jobs/{jobId}` (via `useJobStatus`) until the
 * worker reaches a terminal status, then read the created ids from
 * `job.response.itemIds`. The synchronous path timed out in UAT on large batch
 * clones (up to 100 copies in a serial loop blew the API Gateway 29s timeout).
 */
export interface CloneItemJobResponse {
  id: string;
  type: 'CloneItem';
  status: 'pending' | 'running' | 'completed' | 'failed';
  createdAt: number;
  updatedAt: number;
}

const cloneItemRequest = async (
  orgId: string,
  projectId: string,
  itemId: string,
  data: Partial<CloneItemDto>,
): Promise<CloneItemJobResponse> => {
  const { data: result } = await api.post<unknown, ApiResponse<CloneItemJobResponse>>(
    `/orgs/${orgId}/projects/${projectId}/items/${itemId}/clone`,
    data,
  );
  return result;
};

// Batch clone hook (MT1-688 → async MT1-1426) — used by items list clone dialog.
// Returns the enqueued Job; the dialog polls it to completion.
interface UseCloneItemOptions {
  mutationConfig?: MutationConfig<
    (args: { itemId: string; dto: { count: number } }) => Promise<CloneItemJobResponse>
  >;
}

export const useCloneItem = ({ mutationConfig }: UseCloneItemOptions = {}) => {
  const { orgId, projectId } = useScopeIds('org', 'project');

  return useMutation({
    ...mutationConfig,
    mutationFn: ({ itemId, dto }: { itemId: string; dto: { count: number } }) =>
      cloneItemRequest(orgId, projectId, itemId, dto),
  });
};

// Single clone hook (MT1-1148 → async MT1-1426) — used by sub-items dialog.
// Returns the enqueued Job; the dialog polls it and reads response.itemIds[0].
interface UseCloneSubItemOptions {
  mutationConfig?: MutationConfig<
    (data: { itemId: string; name: string; itemNumber?: string }) => Promise<CloneItemJobResponse>
  >;
}

export const useCloneSubItem = ({ mutationConfig }: UseCloneSubItemOptions = {}) => {
  const { orgId, projectId } = useScopeIds('org', 'project');

  return useMutation({
    ...mutationConfig,
    mutationFn: ({ itemId, ...data }: { itemId: string; name: string; itemNumber?: string }) =>
      cloneItemRequest(orgId, projectId, itemId, data),
  });
};
